import { DeleteRegular, EditRegular, AddRegular } from "@fluentui/react-icons";
import { useMemo } from "react";
import { IAccessUser } from "apps/project-iq/models/IAccessUser";
import { ActionDialog } from "components/actionDialog/ActionDialog";
import {
  IAdminListAction,
  IAdminRowAction,
} from "components/adminList/AdminListPage.types";
import { AdminListPage } from "components/adminList/AdminListPage";
import { useAdminListSort } from "hooks/useAdminListSort";
import { IAccessListSectionProps } from "./AccessListSection.types";

const ACCESS_ACTIONS_COLUMN_WIDTH = "115px";

export const AccessListSection = <T extends { id: string; user: IAccessUser }>({
  cacheKey,
  items,
  isLoading,
  isError,
  columns,
  Dialog,
  dialogProps,
  entityName,
  emptyStateMessage,
  createMutation,
  updateMutation,
  deleteMutation,
  onDialogDismiss,
  onDialogSave,
  onEditRow,
  onDeleteRow,
  onAddNew,
  editingItem,
  isDialogOpen,
  deleteTarget,
  onConfirmDelete,
  onClearDeleteTarget,
}: IAccessListSectionProps<T>): JSX.Element => {
  const isSaving = useMemo(
    () =>
      createMutation.isLoading ||
      updateMutation.isLoading ||
      deleteMutation.isLoading,
    [
      createMutation.isLoading,
      updateMutation.isLoading,
      deleteMutation.isLoading,
    ],
  );

  const {
    sortColumn,
    isSortDescending,
    sortItems,
    onSortColumnChanged,
    onClearColumnChanged,
  } = useAdminListSort<T>({
    cacheKey,
    defaultSortColumn: "name",
    defaultDescending: false,
  });

  const sortedItems = useMemo(
    () => sortItems(items, columns),
    [sortItems, items, columns],
  );

  const rowActions = useMemo<IAdminRowAction<T>[]>(
    () => [
      {
        icon: <EditRegular />,
        tooltip: "Edit",
        onClick: onEditRow,
        disabled: () => isSaving,
      },
      {
        icon: <DeleteRegular />,
        tooltip: "Delete",
        onClick: onDeleteRow,
        disabled: () => isSaving,
      },
    ],
    [onEditRow, onDeleteRow, isSaving],
  );

  const headerActions = useMemo<IAdminListAction[]>(
    () => [
      {
        label: "Add New",
        icon: <AddRegular aria-hidden="true" />,
        appearance: "primary",
        onClick: onAddNew,
      },
    ],
    [onAddNew],
  );

  return (
    <>
      <AdminListPage<T>
        items={sortedItems}
        columns={columns}
        noPadding
        isLoading={isLoading || isSaving}
        rowActions={rowActions}
        headerActions={headerActions}
        getRowId={(item) => item.id}
        actionsWidth={ACCESS_ACTIONS_COLUMN_WIDTH}
        sortColumn={sortColumn}
        isSortDescending={isSortDescending}
        onSortChange={onSortColumnChanged}
        onSortClear={onClearColumnChanged}
        emptyStateMessage={
          isError ? `Error loading ${entityName}` : emptyStateMessage
        }
      />
      <Dialog
        {...dialogProps}
        isOpen={isDialogOpen}
        isSaving={isSaving}
        editingItem={editingItem}
        onSave={onDialogSave}
        onDismiss={onDialogDismiss}
      />
      <ActionDialog
        isVisible={deleteTarget !== null}
        title={`Delete ${entityName}`}
        message={`Remove ${entityName} for "${deleteTarget?.user?.displayName ?? deleteTarget?.user?.userId ?? "this user"}"? This cannot be undone.`}
        confirmationMessage="Delete"
        dismissMessage="Cancel"
        onConfirmation={onConfirmDelete}
        onDismiss={onClearDeleteTarget}
      />
    </>
  );
};
